import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX, Loader2, AlertTriangle, Play } from 'lucide-react';
import { PostMedia } from '../../utils/postMedia';
import { getImageUrl } from '../../services/postService';
import firstPartyAnalytics from '../../services/firstPartyAnalytics';
import AnalyticsEvents from '../../services/analyticsEvents';
import { newMilestones, shouldAutoplay, VIDEO_PLAY_THRESHOLD, formatDuration } from '../../utils/postMedia';
import { t } from '../../i18n';

/**
 * Feed-Video (Marcel 13.10.2026, Reels-Stil): Autoplay stumm sobald ≥ 60 % sichtbar, Pause wenn nicht mehr,
 * Schleife, Poster bis zum Start, Tipp = Ton an/aus, nur EIN Video spielt gleichzeitig (modulweiter Manager),
 * Fortschrittsbalken, Zustände „wird verarbeitet“/„fehlgeschlagen“. Analytics: play (1×), 25/50/75/100 %, unmute.
 * Marcel 13.10.2026 (zweite Runde): Tipp aufs Video = Pause/Weiter (Ton nur über den Button); Video immer KOMPLETT
 * sichtbar (object-contain), die Ränder füllt das Poster unscharf – wie BlurredImg bei Bildern.
 */
let currentPlaying: HTMLVideoElement | null = null;
/** Ton-Wunsch bleibt beim Scrollen über alle Videos erhalten (wie Instagram) */
let soundOn = false;

export function claimPlayback(el: HTMLVideoElement): void {
  if (currentPlaying && currentPlaying !== el) {
    try { currentPlaying.pause(); } catch { /* ignore */ }
  }
  currentPlaying = el;
}
export function releasePlayback(el: HTMLVideoElement): void {
  if (currentPlaying === el) currentPlaying = null;
}

interface PostVideoProps {
  media: PostMedia;
  postId: string;
  /** Vollständig anzeigen (object-contain, Standard) – 'cover' nur für Sonderfälle */
  fit?: 'cover' | 'contain';
  className?: string;
  /** Klick außerhalb des Ton-Buttons; ohne Angabe pausiert ein Tipp das Video bzw. spielt es weiter */
  onClick?: () => void;
}

const PostVideo: React.FC<PostVideoProps> = ({ media, postId, fit = 'contain', className = '', onClick }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [muted, setMuted] = useState(!soundOn);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  /** Vom Nutzer pausiert: kein Autoplay, bis er wieder tippt oder das Video den Bildschirm verlässt */
  const [userPaused, setUserPaused] = useState(false);
  const playedRef = useRef(false);
  const milestonesRef = useRef<Set<number>>(new Set());

  const src = media.status === 'ready' ? getImageUrl(media.file) : null;
  const poster = getImageUrl(media.poster ?? null) ?? undefined;

  // Sichtbarkeit beobachten
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) setVisible(shouldAutoplay(e.intersectionRatio));
    }, { threshold: [0, 0.25, VIDEO_PLAY_THRESHOLD, 0.9] });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Abspielen/Pausieren nach Sichtbarkeit
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src) return;
    if (visible && !userPaused) {
      claimPlayback(v);
      v.muted = muted;
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => { /* Autoplay blockiert → Poster bleibt */ });
    } else {
      try { v.pause(); } catch { /* ignore */ }
      releasePlayback(v);
    }
  }, [visible, src, muted, userPaused]);

  // Verlässt das Video den Bildschirm, gilt die Nutzer-Pause als erledigt (beim nächsten Erscheinen wieder Autoplay)
  useEffect(() => { if (!visible) setUserPaused(false); }, [visible]);

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!src) return;
    setUserPaused((prev) => !prev);
  };

  useEffect(() => () => { const v = videoRef.current; if (v) releasePlayback(v); }, []);

  const onPlay = useCallback(() => {
    setPlaying(true);
    if (!playedRef.current) {
      playedRef.current = true;
      void firstPartyAnalytics.trackEvent(AnalyticsEvents.postVideoPlay, { post_id: postId, video_id: media.videoId ?? null, duration: media.duration ?? null });
    }
  }, [postId, media.videoId, media.duration]);

  const onTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.duration || !Number.isFinite(v.duration)) return;
    const pct = (v.currentTime / v.duration) * 100;
    setProgress(pct);
    for (const m of newMilestones(pct, milestonesRef.current)) {
      milestonesRef.current.add(m);
      void firstPartyAnalytics.trackEvent(AnalyticsEvents.postVideoProgress, { post_id: postId, video_id: media.videoId ?? null, percent: m });
    }
  }, [postId, media.videoId]);

  const onEnded = useCallback(() => {
    // Schleife: Meilensteine je Durchlauf nur einmal → 100 % melden, dann zurücksetzen
    if (!milestonesRef.current.has(100)) {
      milestonesRef.current.add(100);
      void firstPartyAnalytics.trackEvent(AnalyticsEvents.postVideoProgress, { post_id: postId, video_id: media.videoId ?? null, percent: 100 });
    }
    milestonesRef.current = new Set([100]);
  }, [postId, media.videoId]);

  const toggleSound = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !muted;
    setMuted(next);
    soundOn = !next;
    const v = videoRef.current;
    if (v) v.muted = next;
    if (!next) void firstPartyAnalytics.trackEvent(AnalyticsEvents.postVideoUnmute, { post_id: postId, video_id: media.videoId ?? null });
  };

  const fitClass = fit === 'contain' ? 'object-contain' : 'object-cover';

  return (
    <div
      ref={wrapRef}
      data-testid="post-video"
      data-status={media.status ?? 'processing'}
      onClick={onClick ?? togglePlay}
      data-user-paused={userPaused ? 'true' : 'false'}
      className={`relative w-full h-full bg-black cursor-pointer ${className}`}
    >
      {/* Unscharfer Hintergrund aus dem Poster – füllt die Ränder bei object-contain (wie BlurredImg) */}
      {poster && fit === 'contain' && (
        <>
          <img src={poster} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-60 blur-md" loading="lazy" />
          <span className="absolute inset-0 bg-black/30" aria-hidden="true" />
        </>
      )}
      {src ? (
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          muted={muted}
          loop
          playsInline
          preload="metadata"
          onPlay={onPlay}
          onPause={() => setPlaying(false)}
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          className={`relative w-full h-full ${fitClass}`}
          data-testid="post-video-element"
        />
      ) : (
        poster ? <img src={poster} alt="" className={`relative w-full h-full ${fitClass}`} draggable={false} /> : <div className="w-full h-full bg-zinc-900" />
      )}

      {/* Vom Nutzer pausiert: Play-Symbol in der Mitte */}
      {src && userPaused && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center" data-testid="post-video-paused">
          <span className="rounded-full bg-black/55 p-4 text-white backdrop-blur-sm"><Play size={28} fill="currentColor" /></span>
        </div>
      )}

      {/* Verarbeitung / Fehler */}
      {media.status === 'processing' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/40 text-white" data-testid="post-video-processing">
          <Loader2 size={22} className="animate-spin" />
          <span className="text-xs font-semibold">{t('web.component.post-video.wird-verarbeitet')}</span>
        </div>
      )}
      {media.status === 'failed' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/50 text-white" data-testid="post-video-failed">
          <AlertTriangle size={22} />
          <span className="text-xs font-semibold">{t('web.component.post-video.verarbeitung-fehlgeschlagen')}</span>
        </div>
      )}

      {/* Dauer (solange nicht läuft) + Ton */}
      {src && (
        <>
          {!playing && media.duration != null && (
            <span className="absolute left-2 top-2 rounded-md bg-black/60 px-1.5 py-0.5 text-[11px] font-semibold text-white">{formatDuration(media.duration)}</span>
          )}
          <button
            type="button"
            onClick={toggleSound}
            aria-label={muted ? t('web.component.post-video.ton-an') : t('web.component.post-video.ton-aus')}
            data-testid="post-video-sound"
            className="absolute bottom-3 right-2 rounded-full bg-black/60 p-2 text-white backdrop-blur-sm transition hover:bg-black/80"
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/20">
            <div className="h-full bg-[#3fced1]" style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} data-testid="post-video-progress" />
          </div>
        </>
      )}
    </div>
  );
};

export default PostVideo;
